import { Mui } from "@leap4/frontend";
import {FC, useState} from "react";
import EmailChipsInput from "./EmailChipsInput";

const { Button, Checkbox, FormControlLabel, Modal, Typography, Box } = Mui;

interface ScheduleSettingsProps {
    open: boolean;
    onClose: () => void;
    templateId: string | null;
    data: { isActive: boolean, runningDays: string[]; emails?: string[] };
    onSave: (id: string, scheduleData: { isActive: boolean; runningDays: string[]; emails?: string[] }) => void;
}

// Days of the week options for internal use (to be saved in DB)
const daysOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday"
];

//This is referencing that, in the backend, the report scheduler is set to 8:00AM UTC. 
//For display purposes, convert it to Israel time.
const utc8AM = new Date();
utc8AM.setUTCHours(8, 0, 0, 0);
const israelActualTime = new Intl.DateTimeFormat('en-US',{
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZone: 'Asia/Jerusalem'
}).format(utc8AM);

// Display versions with default times (for UI only)
const daysOfWeekDisplay = [
    `${daysOfWeek[0]}, default: ${israelActualTime}`,
    `${daysOfWeek[1]}, default: ${israelActualTime}`,
    `${daysOfWeek[2]}, default: ${israelActualTime}`,
    `${daysOfWeek[3]}, default: ${israelActualTime}`,
    `${daysOfWeek[4]}, default: ${israelActualTime}`,
    `${daysOfWeek[5]}, default: ${israelActualTime}`,
    `${daysOfWeek[6]}, default: ${israelActualTime}`,
];

// Map display strings to simple day names and vice versa
const displayToDayMap = daysOfWeekDisplay.reduce((acc, display, index) => {
    acc[display] = daysOfWeek[index];
    return acc;
}, {} as Record<string, string>);

const dayToDisplayMap = daysOfWeek.reduce((acc, day, index) => {
    acc[day] = daysOfWeekDisplay[index];
    return acc;
}, {} as Record<string, string>);

const style = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    minWidth: 400,
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
};

const ScheduleSettings: FC<ScheduleSettingsProps> = ({open, onClose, templateId, onSave, data}) => {
    // Convert incoming day-only values to display values
    const convertToDisplayDays = (days: string[]): string[] => {
        return days.map(day => dayToDisplayMap[day] || day);
    };
    
    // Convert display values to day-only values for saving
    const convertToDatabaseDays = (displayDays: string[]): string[] => {
        return displayDays.map(display => displayToDayMap[display] || display);
    };

    const [selectedDays, setSelectedDays] = useState<string[]>(convertToDisplayDays(data.runningDays));
    const [emails, setEmails] = useState<string[]>(data.emails);
    const [schedulerActive, setSchedulerActive] = useState<boolean>(data.isActive);

    const handleDayChange = (dayValue: string) => {
        setSelectedDays((prev) =>
            prev.includes(dayValue) ? prev.filter((day) => day !== dayValue) : [...prev, dayValue]
        );
    };

    const handleSave = () => {
        if (!templateId) return;
        const isActive = schedulerActive && selectedDays.length > 0;
        // Convert display days to database days before saving
        const databaseDays = convertToDatabaseDays(selectedDays);
        onSave(templateId, {isActive, runningDays: databaseDays, emails: emails});
        onClose();
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={style}>
                <Typography variant="h6" mb={2}>
                    Schedule Settings
                </Typography>
                <Typography variant="subtitle1">Select the days of the week:</Typography>
                <Box sx={{display: "flex", flexDirection: "column", mb: 2}}>
                    {daysOfWeekDisplay.map((day) => (
                        <FormControlLabel
                            key={day}
                            control={
                                <Checkbox
                                    checked={selectedDays.includes(day)}
                                    onChange={() => handleDayChange(day)}
                                />
                            }
                            label={day}
                        />
                    ))}
                </Box>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={schedulerActive}
                            onChange={(e) => setSchedulerActive(e.target.checked)}
                        />
                    }
                    label="Scheduler Active"
                />
                <Typography variant="subtitle1" sx={{mb: 1}}>
                    Emails to receive the report:
                </Typography>
                <EmailChipsInput emails={emails} setEmails={setEmails}/>
                <Box sx={{display: "flex", justifyContent: "flex-end", mt: 2}}>
                    <Button variant="contained" color="primary" onClick={handleSave}>
                        Save
                    </Button>
                    <Button variant="outlined" color="secondary" onClick={onClose} sx={{ml: 2}}>
                        Cancel
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default ScheduleSettings;
